import { EFinancingProgram, ELoanPurpose, EOriginSystemId } from '@if/core/types'
import * as yup from '@if/core/yup'
import type { FormDraft } from '@if/core/yup'
import type { IfUser } from '@if/mui'
import { Banner, ControlledCheckbox, FormContainer, useCan, useConfirmDialog, useFormDialog } from '@if/mui'
import { access } from '@src/access'
import { useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import {
  usePostPersonalLoanWorksheet,
  usePutPersonalLoanWorksheet,
} from '@src/data/api/worksheet-api/personal-loan-worksheet-api'
import { useWorksheetFormDefaultsSeed } from '@src/data/api/worksheet-api/worksheet-form-defaults-api'
import { createNewPersonalLoanWorksheetDto, updatePersonalLoanWorksheet } from '@src/data/worksheet-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import {
  EditPersonalLoanWorksheetDto,
  buildEditPersonalLoanWorksheetDtoSchema,
  sumMerchantPayments,
} from '@src/types/WorksheetSchema'
import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { usePaymentMerchantList } from '../ViewCreditApplicationPage/worksheet-hooks'
import PersonalLoanWorksheetBody from './components/PersonalLoanWorksheetBody'
import WorksheetResetAction from './components/WorksheetResetAction'
import WorksheetSkeleton from './components/WorksheetSkeleton'

interface Props {
  merchant: Merchant
  creditApplication: CreditApplication
  user: IfUser
  merchantsListFromParentId: Merchant[]
  merchantPayments: { label: string; value: string; id: string }[]
}

const PersonalLoanWorksheet = ({
  merchant,
  creditApplication,
  user,
  merchantsListFromParentId,
  merchantPayments,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [merchantFinancingConfig, isLoadingConfig] = useMerchantFinancingConfig(
    merchant?.id,
    EFinancingProgram.Personal,
  )
  const isMedicardServiceCategory =
    merchant?.serviceCategory === ELoanPurpose.Medical ||
    merchant?.serviceCategory === ELoanPurpose.Dentistry ||
    merchant?.serviceCategory === ELoanPurpose.Aesthetics

  const defaultConfig = FinancingProgramConfigs[EFinancingProgram.Personal]
  const categoryBasedMax = isMedicardServiceCategory ? defaultConfig.maxLoanAmountMedicard : defaultConfig.maxLoanAmount
  const maxLoanAmount = Math.min(categoryBasedMax, merchantFinancingConfig?.maximumLoanAmount ?? categoryBasedMax)
  const minLoanAmount = merchantFinancingConfig?.minimumLoanAmount ?? defaultConfig.minLoanAmount
  const maxTerm = merchantFinancingConfig?.maxLoanTerm ?? defaultConfig.maxTerm
  const minTerm = merchantFinancingConfig?.minLoanTerm ?? defaultConfig.minTerm
  const longTerm = merchantFinancingConfig?.longTerm ?? defaultConfig.longTerm
  const largeLoanAmount = merchantFinancingConfig?.largeLoanAmount ?? defaultConfig.largeLoanAmount
  const configOverride = { minLoanAmount, maxLoanAmount, maxTerm, minTerm, longTerm, largeLoanAmount }

  const isBuydownEnabled = useCan(access.termAndPlanSelection)
  // Accepted `plan|term` pairs lifted from the body; an empty set disables the schema rule.
  const [validTermAndPlanKeys, setValidTermAndPlanKeys] = useState<Set<string>>(new Set())
  const { seedWorksheetFormDefaults } = useWorksheetFormDefaultsSeed()

  const EditPersonalLoanWorksheetDtoSchema = buildEditPersonalLoanWorksheetDtoSchema(
    creditApplication.applicant.hardHitReportReceivedOn!,
    creditApplication.finalCreditDecision.maxAmountFinanced!,
    creditApplication.merchantPaymentPlanId,
    configOverride,
    validTermAndPlanKeys,
  )

  const shouldSeedFromApi = isBuydownEnabled && !creditApplication.worksheet && !!creditApplication.id

  // Cast the loaded worksheet through the schema so its wire-string dates become
  // `DateOnly` (what the pickers require) — symmetric with the create branch's
  // `getDefault()`, which already yields `DateOnly`.

  const defaultValues = shouldSeedFromApi
    ? async () => {
        const seed = EditPersonalLoanWorksheetDtoSchema.getDefault() as EditPersonalLoanWorksheetDto
        const defaults = await seedWorksheetFormDefaults({
          creditApplicationId: creditApplication.id,
          originSystemId: EOriginSystemId.MerchantDashboard,
        })
        return { ...seed, amountRequested: defaults.amountRequested }
      }
    : creditApplication.worksheet
      ? creditApplication.worksheet
      : EditPersonalLoanWorksheetDtoSchema.getDefault()

  const [postWorksheet] = usePostPersonalLoanWorksheet()
  const [putWorksheet] = usePutPersonalLoanWorksheet()

  const { merchantList, setMerchantList } = usePaymentMerchantList(
    creditApplication,
    merchantsListFromParentId,
    merchant,
    user,
  )

  const hasCVT = Boolean(creditApplication.contract.cvtNumber)
  const [isAprTooHigh, setIsAprTooHigh] = useState(false)
  const [maxPmtExceeded, setMaxPmtExceeded] = useState(false)
  const [fundingReady, setFundingReady] = useState(false)

  // Existing contract (CVT): re-creating cancels it, so ask before overwriting.
  const cancelContractDialog = useConfirmDialog({
    mode: 'destructive',
    title: t('worksheet.cancelContractTitle'),
    message: t('worksheet.willCancelContract'),
    confirmLabel: t('common.confirm'),
  })

  // High Estimate: the merchant must confirm they explained the concept (a gated checkbox) before saving.
  const highEstimateDialog = useFormDialog<{ agreed: boolean }>({
    title: t('worksheet.highEstimateConfirmationTitle'),
    confirmLabel: t('common.confirm'),
    schema: yup.object({
      agreed: yup.boolean().oneOf([true], t('worksheet.highEstimateConfirmationText')),
    }),
    defaultValues: {},
    content: ({ form }) => (
      <ControlledCheckbox name="agreed" control={form.control} label={t('worksheet.highEstimateConfirmationText')} />
    ),
  })

  const saveWorksheet = async (
    formData: EditPersonalLoanWorksheetDto,
    methods: UseFormReturn<FormDraft<EditPersonalLoanWorksheetDto>>,
  ) => {
    if (!fundingReady) {
      return false
    }
    if (isAprTooHigh || maxPmtExceeded) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return false
    }
    if (sumMerchantPayments(formData.merchantPayments) !== formData.amountRequested) {
      methods.setError('amountRequested', { type: 'validate', message: t('common.errors.notEqualMerchantPayment') })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return false
    }
    if (hasCVT && merchant.canAcceptHighEstimate) {
      const confirmed = await cancelContractDialog.open()
      if (!confirmed) return false
    }
    if (formData.isHighEstimate) {
      const confirmed = await highEstimateDialog.create()
      if (!confirmed) return false
      formData.highEstimateExplainedToClient = true
    }

    merchantList.forEach((merch, index) => {
      formData.merchantPayments[index].merchantId = merch.id
      formData.merchantPayments[index].paymentMethod = merch.defaultPaymentMethod
    })
    if (creditApplication.worksheet == null) {
      await postWorksheet({ worksheet: createNewPersonalLoanWorksheetDto(creditApplication.id, formData) })
    } else {
      await putWorksheet({ worksheet: updatePersonalLoanWorksheet(formData, creditApplication) })
    }
    navigate(`/credits/${creditApplication.financingProgramId}/${creditApplication.id}`)
  }

  if (isLoadingConfig) {
    return <WorksheetSkeleton />
  }

  return (
    <>
      {isAprTooHigh && (
        <Banner severity="error" title={t('common.error')} sx={{ mb: 2 }}>
          {t('worksheet.aprTooHighError')}
        </Banner>
      )}

      <FormContainer
        mode={creditApplication.worksheet ? 'edit' : 'create'}
        loadingContent={<WorksheetSkeleton />}
        useFormSetup={() => ({
          schema: EditPersonalLoanWorksheetDtoSchema,
          defaultValues: defaultValues as unknown as EditPersonalLoanWorksheetDto,
        })}
        onSubmit={saveWorksheet}
        successMessage={t('worksheet.saveSuccess')}
        pageActions
        submitLabel={t('worksheet.createContract')}
        actions={<WorksheetResetAction />}
      >
        <PersonalLoanWorksheetBody
          merchant={merchant}
          minTerm={minTerm}
          maxTerm={maxTerm}
          longTerm={longTerm}
          largeLoanAmount={largeLoanAmount}
          creditApplication={creditApplication}
          user={user}
          merchantPayments={merchantPayments}
          merchantsListFromParentId={merchantsListFromParentId}
          merchantList={merchantList}
          setMerchantList={setMerchantList}
          onAprTooHighChange={setIsAprTooHigh}
          onMaxPmtExceededChange={setMaxPmtExceeded}
          onFundingReadyChange={setFundingReady}
          onValidTermAndPlanKeysChange={setValidTermAndPlanKeys}
        />
      </FormContainer>
    </>
  )
}

export default PersonalLoanWorksheet
